import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../allauth/auth';
import AssignmentActions from '../components/AssignmentActions';
import PaymentAssignmentCard from '../components/PaymentAssignmentCard';
import ApiClient from '../config/ApiClient';
import { PaginatedResponse, PaymentAssignments } from '../object-actions/types/types';

const PaymentAssignmentsPage: React.FC = () => {
    const auth = useAuth();
    const user = auth?.data?.user;
    const [assignments, setAssignments] = useState<PaymentAssignments[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchAssignments = useCallback(async () => {
        if (!user?.id) {
            setError('User not authenticated');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            params.set('payment__author', user.id.toString());
            if (limit > 0) params.set('limit', limit.toString());
            if (offset > 0) params.set('offset', offset.toString());

            const response = await ApiClient.get<PaginatedResponse<PaymentAssignments>>(
                `/api/payment-assignments/?${params.toString()}`
            );

            if (response.error) {
                setError(response.error);
            } else if (response.success && response.data) {
                const data = response.data;
                if (offset === 0) {
                    setAssignments(data.results || []);
                } else {
                    setAssignments(prev => [...prev, ...(data.results || [])]);
                }
                setTotalCount(data.count || 0);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch payment assignments');
        } finally {
            setLoading(false);
        }
    }, [user?.id, limit, offset]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    const handleUpdate = useCallback(() => {
        // Reset and refetch assignments
        setOffset(0);
        fetchAssignments();
    }, [fetchAssignments]);

    if (loading && assignments.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Card>
                    <CardContent>
                        <Typography color="error">{error}</Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Payment Assignments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage assignments for your payments
            </Typography>

            {assignments.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary" textAlign="center">
                            No payment assignments found.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {assignments.map((assignment) => {
                        return (
                            <Card key={assignment.id} elevation={2}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography variant="h6" component="h2">
                                            Assignment #{assignment.id}
                                        </Typography>
                                        <AssignmentActions
                                            assignment={assignment}
                                            onUpdate={handleUpdate}
                                        />
                                    </Box>
                                    <PaymentAssignmentCard assignment={assignment} />
                                    {assignment.payment && (
                                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Organization: {assignment.organization?.str} |
                                                Pre Assessment: {assignment.assessment_id ? `#${assignment.assessment_id}` : assignment.pre_assessment?.str ?? '-'} |
                                                Payment: #{assignment.payment.id}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            {totalCount > assignments.length && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {assignments.length} of {totalCount} assignments
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default PaymentAssignmentsPage;

